import { motion } from 'framer-motion';
import { META, ESSAYS, PRESS } from './data';

// HOME — newspaper front page. Dropcap lede letter. NOT a portrait split hero.
export default function Home({ setPage }) {
  const lead = ESSAYS[0];
  return (
    <div className="bg-[#F4EEE0] text-[#1A1410]">
      {/* MASTHEAD STRIP */}
      <section className="pt-28 max-w-[1500px] mx-auto px-8 lg:px-14">
        <div className="border-y-[3px] border-[#1A1410] py-4 flex flex-wrap items-baseline justify-between gap-4 font-mono text-[10px] tracking-[0.3em] uppercase text-[#1A1410]/65">
          <span>{META.paper}</span>
          <span className="hidden md:inline">{META.issue}</span>
          <span>One Dollar — All Subscribers — No Advertising</span>
        </div>
      </section>

      {/* LETTER FRONT PAGE — three-column dropcap lede */}
      <section className="max-w-[1500px] mx-auto px-8 lg:px-14 pt-10 pb-12">
        <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#7B1A1A] mb-4 text-center">— A letter from the desk of —</p>
        <motion.h1 initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="font-serif italic text-center text-[60px] sm:text-[100px] lg:text-[150px] leading-[0.9] text-[#1A1410]">
          On the cost of <span className="not-italic">always</span><br />answering quickly.
        </motion.h1>
        <p className="font-mono text-[10px] tracking-[0.32em] uppercase text-[#1A1410]/55 mt-6 text-center">By {META.byline} · Issue 47 · 8 minute read</p>
      </section>

      {/* THREE-COLUMN BODY WITH DROPCAP */}
      <section className="max-w-[1300px] mx-auto px-8 lg:px-14 pb-12">
        <div className="grid md:grid-cols-3 gap-x-10 gap-y-6 font-serif text-base leading-[1.7] text-[#1A1410]/85">
          <div>
            <p>
              <span className="float-left font-serif italic text-[88px] leading-[0.78] mr-2 mt-1 text-[#7B1A1A]">D</span>
              ear reader, this letter is being written on a Friday afternoon in Brooklyn, with a half-cold espresso, and the strange quiet that arrives only after I have put my phone in the drawer of an old desk a friend gave me.
            </p>
            <p className="mt-4">A senior partner at one of New York's largest firms told me last month that her single most useful habit is, simply, to <em>not</em> answer her email until Friday afternoon.</p>
          </div>
          <div>
            <p>It is a small thing, and an unfashionable one. We spend so much of our coaching engagements pretending that responsiveness is a virtue. It is not. It is a tax. It is a tax on the thinking that lives behind our work, the kind of thinking that asks better questions and makes longer-lasting decisions.</p>
            <p className="mt-4">She arrives at her desk at seven each Friday. She closes her door. She reads, in long uninterrupted hours, every message that arrived in the prior week. She writes, by hand, the half dozen replies that genuinely mattered. She <em>throws away</em> the rest.</p>
            <p className="mt-4">Her colleagues have, over the last four years, learned not to wait.</p>
          </div>
          <div>
            <p>I have been thinking about this all week, because three different founders this month have told me they cannot do their best work between Monday and Wednesday — and not one of them has thought to ask why.</p>
            <p className="mt-4">The answer, I suspect, is that they have no Fridays. They have only inboxes. And the inbox, as my partner from New York would tell you, is somebody else's priority list. Not yours.</p>
            <p className="mt-4 italic text-[#7B1A1A]">— M. V.</p>
            <p className="mt-3 font-mono text-[10px] tracking-[0.3em] uppercase text-[#1A1410]/55">Continued in §II · Field Notes</p>
          </div>
        </div>
      </section>

      {/* HANDWRITTEN SIGNATURE BLOCK */}
      <section className="max-w-[1300px] mx-auto px-8 lg:px-14 pb-14 border-y border-[#1A1410]/30 py-8 grid md:grid-cols-[1fr_auto] gap-10 items-center">
        <div>
          <p className="font-serif italic text-2xl text-[#1A1410]">Yours, on Friday afternoon —</p>
          <p className="font-serif italic text-[42px] text-[#7B1A1A] leading-none mt-2" style={{ fontFamily: '"Brush Script MT", "Snell Roundhand", cursive' }}>Mira Vance</p>
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#1A1410]/55 mt-2">Brooklyn · February 9, 2026</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button onClick={() => setPage('notes')} data-testid="mv-home-cta-notes" className="font-mono text-[10px] tracking-[0.32em] uppercase border border-[#1A1410] text-[#1A1410] px-6 py-3 hover:bg-[#1A1410] hover:text-[#F4EEE0] transition-colors">§ II · Read more →</button>
          <button onClick={() => setPage('apply')} data-testid="mv-home-cta-apply" className="font-mono text-[10px] tracking-[0.32em] uppercase bg-[#7B1A1A] text-[#F4EEE0] px-6 py-3 hover:bg-[#1A1410] transition-colors">§ IV · Write back →</button>
        </div>
      </section>

      {/* PRESS — newspaper "as quoted in" strip */}
      <section className="bg-[#1A1410] text-[#F4EEE0] py-8">
        <div className="max-w-[1500px] mx-auto px-8 lg:px-14">
          <p className="font-mono text-[10px] tracking-[0.32em] uppercase text-[#C9A06A] mb-4">— Quoted & reprinted in —</p>
          <div className="flex flex-wrap items-baseline gap-x-8 gap-y-3">
            {PRESS.map(p => (
              <span key={p} className="font-serif italic text-lg lg:text-xl text-[#F4EEE0]/85">{p}</span>
            ))}
          </div>
        </div>
      </section>

      {/* PREVIEW STRIP — most recent issues */}
      <section className="max-w-[1500px] mx-auto px-8 lg:px-14 py-14">
        <div className="flex items-baseline justify-between mb-7">
          <p className="font-mono text-[10px] tracking-[0.32em] uppercase text-[#7B1A1A]">— On the second page · Recent issues</p>
          <button onClick={() => setPage('notes')} className="font-mono text-[10px] tracking-[0.32em] uppercase border-b border-[#1A1410] text-[#1A1410] hover:border-[#7B1A1A] hover:text-[#7B1A1A]">All field notes →</button>
        </div>
        <div className="grid md:grid-cols-3 gap-px bg-[#1A1410]/15">
          {ESSAYS.slice(1, 4).map(e => (
            <article key={e.iss} className="bg-[#F4EEE0] p-7">
              <p className="font-mono text-[10px] tracking-[0.32em] uppercase text-[#7B1A1A]">No. {e.iss} · {e.cat}</p>
              <h3 className="font-serif italic text-2xl mt-2 leading-snug">{e.t}</h3>
              <p className="font-serif text-sm text-[#1A1410]/65 mt-3 leading-[1.7]">{e.d}</p>
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#1A1410]/45 mt-4">{e.date} · {e.read}</p>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
